import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Container,
  Grid,
  Box,
  styled,
  TextField,
  InputAdornment,
  Chip,
  Skeleton,
} from "@mui/material";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import allArticlesData from "../../data/blogPosts.json";
import useSEO from "../../hooks/BlogUseSeo";
import { generateBlogListingSEO } from "../../hooks/BlogSeoUtils";

// Sort articles by date descending (newest first)
const allArticles = [...allArticlesData].sort((a, b) => {
  return new Date(b.date) - new Date(a.date);
});

const StyledLink = styled(Link)(({ theme }) => ({
  color: "inherit",
  textDecoration: "none",
  "&:hover": {
    textDecoration: "none",
  },
}));

// Updated with dark theme
const PageNumber = styled(Box)(({ theme, active }) => ({
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  cursor: "pointer",
  background: active
    ? "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)"
    : "rgba(255,255,255,0.05)",
  border: active ? "none" : "1px solid rgba(255,255,255,0.1)",
  color: "#fff",
  fontWeight: active ? 600 : 400,
  transition: "all 0.3s ease",
  "&:hover": {
    background: active
      ? "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)"
      : "rgba(255,255,255,0.1)",
    transform: "translateY(-2px)",
  },
}));

const PageNavigation = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  cursor: "pointer",
  backgroundColor: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "rgba(255,255,255,0.7)",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#fff",
  },
}));

// Glassmorphism card styling
const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 16,
  overflow: "hidden",
  background: "rgba(255,255,255,0.03)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.08)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-8px)",
    borderColor: "rgba(226, 104, 74, 0.3)",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3), 0 0 30px rgba(226, 104, 74, 0.1)",
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 220,
  transition: "transform 0.5s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

// Updated category chip with gradient background
const CategoryChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: 12,
  left: 12,
  background: "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)",
  color: "white",
  fontWeight: 600,
  fontSize: "0.7rem",
  height: "26px",
  border: "none",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
}));

// Dark theme search box with glow
const SearchBox = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 16,
    backgroundColor: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(10px)",
    color: "#fff",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.1)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.2)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#E2684A",
      boxShadow: "0 0 20px rgba(226, 104, 74, 0.2)",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    "&::placeholder": {
      color: "rgba(255,255,255,0.5)",
      opacity: 1,
    },
  },
}));

const CategoryButton = memo(
  ({ category, activeCategory, handleCategoryClick }) => (
    <Box
      onClick={() => handleCategoryClick(category)}
      sx={{
        px: 3,
        py: 1,
        borderRadius: "14px",
        background:
          activeCategory === category
            ? "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)"
            : "rgba(255,255,255,0.05)",
        border:
          activeCategory === category
            ? "none"
            : "1px solid rgba(255,255,255,0.1)",
        color: "#fff",
        cursor: "pointer",
        fontWeight: 500,
        fontSize: "0.9rem",
        transition: "all 0.3s ease",
        "&:hover": {
          background:
            activeCategory === category
              ? "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)"
              : "rgba(255,255,255,0.1)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {category}
    </Box>
  )
);

const BlogCard = memo(({ article }) => {
  const getPrimaryCategory = useCallback((article) => {
    if (!article.categories || article.categories.length === 0) {
      return null;
    }
    return (
      article.categories.find((cat) => cat !== "All") || article.categories[0]
    );
  }, []);

  return (
    <StyledLink to={`/blog/${article.id}`}>
      <StyledCard>
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          <StyledCardMedia
            component="img"
            image={article.image}
            alt={article.title}
            loading="lazy"
          />
          {getPrimaryCategory(article) && (
            <CategoryChip label={getPrimaryCategory(article)} size="small" />
          )}
        </Box>
        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "transparent",
            p: 3,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: "1.1rem",
              lineHeight: 1.4,
              color: "#fff",
              mb: 2,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {article.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: "auto",
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.85rem",
            }}
          >
            {article.date} • {article.readTime}
          </Typography>
        </CardContent>
      </StyledCard>
    </StyledLink>
  );
});

const NoResults = memo(() => (
  <Box sx={{ textAlign: "center", my: 8 }}>
    <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.7)", mb: 2 }}>
      No articles found matching your search.
    </Typography>
    <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.5)" }}>
      Try different keywords or browse all our articles.
    </Typography>
  </Box>
));

const BlogPaginationComponent = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedArticles, setDisplayedArticles] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const ITEMS_PER_PAGE = 9;

  const categories = useMemo(
    () => ["All", "AI Agents", "Marketing & Sales", "Voice", "OCR"],
    []
  );

  const seoData = useMemo(
    () => generateBlogListingSEO(activeCategory, "https://www.beanbag.ai"),
    [activeCategory]
  );

  useSEO(seoData);

  const handleCategoryClick = useCallback((category) => {
    setActiveCategory(category);
    setSearchTerm("");

    const filtered =
      category === "All"
        ? allArticles
        : allArticles.filter(
          (article) =>
            article.categories && article.categories.includes(category)
        );

    setFilteredArticles(filtered);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timerId);
  }, []);

  useEffect(() => {
    const debouncedSearch = setTimeout(() => {
      const filtered = searchTerm
        ? allArticles.filter(
          (article) =>
            article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (article.content &&
              article.content
                .toLowerCase()
                .includes(searchTerm.toLowerCase()))
        )
        : activeCategory === "All"
          ? allArticles
          : allArticles.filter(
            (article) =>
              article.categories && article.categories.includes(activeCategory)
          );

      setFilteredArticles(filtered);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(debouncedSearch);
  }, [searchTerm, activeCategory]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setDisplayedArticles(filteredArticles.slice(startIndex, endIndex));

    if (currentPage > 1) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [currentPage, filteredArticles]);

  const totalPages = useMemo(
    () => Math.ceil(filteredArticles.length / ITEMS_PER_PAGE),
    [filteredArticles.length, ITEMS_PER_PAGE]
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const renderPagination = useCallback(() => {
    const pages = [];

    if (currentPage > 1) {
      pages.push(
        <PageNavigation
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          sx={{ mr: 1 }}
        >
          ‹
        </PageNavigation>
      );
    }

    for (let i = 1; i <= Math.min(5, totalPages); i++) {
      pages.push(
        <PageNumber
          key={i}
          active={currentPage === i ? 1 : 0}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageNumber>
      );
    }

    if (totalPages > 6) {
      pages.push(
        <Box key="ellipsis" sx={{ mx: 1, color: "rgba(255,255,255,0.5)" }}>
          ...
        </Box>
      );
    }

    if (totalPages > 5) {
      pages.push(
        <PageNumber
          key={totalPages}
          active={currentPage === totalPages ? 1 : 0}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </PageNumber>
      );
    }

    if (currentPage < totalPages) {
      pages.push(
        <PageNavigation
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          sx={{ ml: 1 }}
        >
          ›
        </PageNavigation>
      );
    }

    return pages;
  }, [currentPage, totalPages, handlePageChange]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#030014",
        pt: { xs: 12, md: 16 },
        pb: 8,
        position: "relative",
      }}
    >
      {/* Background gradients */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "20%",
          width: "500px",
          height: "500px",
          background: "radial-gradient(circle, rgba(124, 58, 237, 0.15) 0%, transparent 70%)",
          filter: "blur(80px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "40%",
          right: "10%",
          width: "400px",
          height: "400px",
          background: "radial-gradient(circle, rgba(226, 104, 74, 0.1) 0%, transparent 70%)",
          filter: "blur(80px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          {isLoading ? (
            <>
              <Skeleton
                variant="text"
                height={80}
                width="60%"
                sx={{ mx: "auto", mb: 2, bgcolor: "rgba(255,255,255,0.05)" }}
              />
              <Skeleton
                variant="text"
                height={30}
                width="80%"
                sx={{ mx: "auto", mb: 1, bgcolor: "rgba(255,255,255,0.05)" }}
              />
            </>
          ) : (
            <>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#22D3EE",
                  mb: 2,
                  textTransform: "uppercase",
                  letterSpacing: 2,
                  fontWeight: 600,
                }}
              >
                INSIGHTS & RESOURCES
              </Typography>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "white",
                  mb: 3,
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                }}
              >
                {activeCategory !== "All"
                  ? `${activeCategory} Articles`
                  : "Blog"}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  maxWidth: "700px",
                  mx: "auto",
                  mb: 5,
                  fontSize: { xs: "1rem", md: "1.15rem" },
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                Explore our collection of articles, tutorials, and insights about
                AI, machine learning, and how to transform your business.
              </Typography>
            </>
          )}

          <SearchBox
            fullWidth
            placeholder="Search articles..."
            variant="outlined"
            onChange={handleSearch}
            value={searchTerm}
            sx={{ maxWidth: "500px", mx: "auto", mb: 4 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "rgba(255,255,255,0.5)" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2,
            }}
          >
            {categories.map((category) => (
              <CategoryButton
                key={category}
                category={category}
                activeCategory={activeCategory}
                handleCategoryClick={handleCategoryClick}
              />
            ))}
          </Box>
        </Box>

        {isLoading ? (
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: "100%", bgcolor: "rgba(255,255,255,0.03)", borderRadius: 4 }}>
                  <Skeleton variant="rectangular" height={220} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
                  <CardContent>
                    <Skeleton variant="text" height={30} width="80%" sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
                    <Skeleton variant="text" height={20} width="40%" sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : displayedArticles.length > 0 ? (
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {displayedArticles.map((article) => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <BlogCard article={article} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <NoResults />
        )}

        {filteredArticles.length > ITEMS_PER_PAGE && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              mt: 6,
            }}
          >
            {renderPagination()}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default BlogPaginationComponent;
